import {
  AccountCircle,
  BuildCircle,
  Business,
  Dashboard,
  Favorite,
  Login,
  Logout,
  Menu as MenuIcon,
  PersonAdd,
  ShoppingBag,
  ShoppingCart,
  Speed,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useI18n } from '../../context/I18nContext';
import { prefetchRoute } from '../../prefetch/routes';
import SearchAutocomplete from '../common/SearchAutocomplete';

// Helper to get avatar URL
const getAvatarSrc = (avatar) => {
  if (!avatar) return undefined;

  // R2 URL - resolve full URL
  if (avatar.startsWith('/api/images/')) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    return baseUrl ? `${baseUrl.replace(/\/api\/?$/, '')}${avatar}` : avatar;
  }

  // External URL or base64
  if (avatar.startsWith('http') || avatar.startsWith('data:')) {
    return avatar;
  }

  return undefined;
};

const Header = ({ userType = 'b2c' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const {
    user,
    isAuthenticated,
    logout,
    getUserRoleDisplayName,
    hasB2BAccess,
    isAdmin,
  } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const { totalItems } = useCart();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleMenuClose();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Navigation items based on user type
  const getNavigationItems = () => {
    const baseItems = [
      { label: t('nav.catalog'), path: '/products', icon: <ShoppingBag /> },
      {
        label: t('nav.setConfigurator'),
        path: '/set-configurator',
        icon: <BuildCircle />,
      },
    ];

    if (userType === 'b2b' && hasB2BAccess()) {
      return [
        { label: t('nav.b2b'), path: '/b2b/products', icon: <Business /> },
        { label: 'Quick Order', path: '/b2b/quick-order', icon: <Speed /> },
        { label: 'Dashboard', path: '/b2b/dashboard', icon: <Dashboard /> },
        ...baseItems,
      ];
    }

    if (userType === 'admin' && isAdmin()) {
      return [
        ...baseItems,
        {
          label: t('nav.admin'),
          path:
            (typeof import.meta !== 'undefined' &&
              import.meta.env?.VITE_ADMIN_APP_URL) ||
            'https://wasser-pro-admin.pages.dev',
          icon: <Dashboard />,
          external: true,
        },
      ];
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  return (
    <>
      <AppBar
        position="sticky"
        color={userType === 'b2b' ? 'primary' : 'default'}
        elevation={1}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Открыть меню"
                onClick={toggleMobileMenu}
                sx={{
                  mr: 1,
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Typography
              variant={isSmallMobile ? 'subtitle1' : 'h6'}
              component={Link}
              to="/"
              sx={{
                flexGrow: 0,
                textDecoration: 'none',
                color: 'inherit',
                fontWeight: 600,
                mr: isMobile ? 1 : 4,
                fontSize: isSmallMobile ? '0.95rem' : undefined,
              }}
            >
              {isSmallMobile ? 'Furniture' : 'Furniture Excellence'}
            </Typography>

            {/* User type indicator */}
            {user && !isMobile && (
              <Chip
                label={getUserRoleDisplayName()}
                color={userType === 'b2b' ? 'secondary' : 'primary'}
                size="small"
                sx={{ mr: 2 }}
              />
            )}

            {/* Desktop navigation */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', ml: 4 }}>
                <Button
                  color="inherit"
                  component={Link}
                  to="/"
                  onMouseEnter={() => prefetchRoute('/')}
                  onFocus={() => prefetchRoute('/')}
                  sx={{ mr: 2 }}
                >
                  {t('nav.home')}
                </Button>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    color="inherit"
                    component={item.external ? 'a' : Link}
                    {...(item.external
                      ? {
                          href: item.path,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : { to: item.path })}
                    onMouseEnter={() => {
                      if (!item.external) prefetchRoute(item.path);
                    }}
                    onFocus={() => {
                      if (!item.external) prefetchRoute(item.path);
                    }}
                    sx={{ mr: 2 }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Search - Desktop */}
            {!isMobile && (
              <Box sx={{ mr: 2, flexGrow: 1, maxWidth: 400 }}>
                <SearchAutocomplete
                  size="small"
                  placeholder="Поиск товаров..."
                />
              </Box>
            )}

            {/* Favorites button - only on mobile */}
            {isMobile && (
              <IconButton
                color="inherit"
                component={Link}
                to="/favorites"
                aria-label="Избранное"
                sx={{
                  mr: 0.5,
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                <Favorite />
              </IconButton>
            )}

            {/* Cart button */}
            <IconButton
              color="inherit"
              component={Link}
              to="/cart"
              aria-label="Корзина"
              sx={{
                mr: isMobile ? 0.5 : 2,
                minWidth: 44,
                minHeight: 44,
              }}
            >
              <Badge badgeContent={totalItems} color="error">
                <ShoppingCart />
              </Badge>
            </IconButton>

            {/* User menu */}
            {isAuthenticated ? (
              <>
                {!isMobile && user && (
                  <Typography
                    variant="body2"
                    sx={{
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 500,
                    }}
                  >
                    {getUserRoleDisplayName()} | {user.email}
                  </Typography>
                )}
                <IconButton
                  color="inherit"
                  onClick={handleProfileMenuOpen}
                  aria-label="Открыть меню профиля"
                  sx={{
                    p: 0.5,
                    minWidth: 44,
                    minHeight: 44,
                  }}
                >
                  <Avatar
                    src={getAvatarSrc(user?.avatar)}
                    sx={{
                      width: isSmallMobile ? 28 : 32,
                      height: isSmallMobile ? 28 : 32,
                      bgcolor: 'primary.main',
                    }}
                  >
                    {user?.firstName?.[0]?.toUpperCase() ||
                      user?.email?.[0]?.toUpperCase() || <AccountCircle />}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      minWidth: 180,
                      '& .MuiMenuItem-root': {
                        minHeight: 48,
                        py: 1.5,
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/profile"
                  >
                    {t('header.profile')}
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/orders"
                  >
                    {t('header.myOrders')}
                  </MenuItem>
                  {user && user.role === 'b2c_customer' && (
                    <MenuItem
                      onClick={handleMenuClose}
                      component={Link}
                      to="/b2b/apply"
                    >
                      {t('header.applyB2B')}
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    {t('nav.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              !isMobile && (
                <Box>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/login"
                    sx={{ mr: 1, minHeight: 44 }}
                  >
                    {t('nav.login')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    component={Link}
                    to="/register"
                    sx={{ minHeight: 44 }}
                  >
                    {t('header.register')}
                  </Button>
                </Box>
              )
            )}
            {/* Locale toggle */}
            <Button
              size="small"
              sx={{
                ml: isMobile ? 0.5 : 2,
                minWidth: isSmallMobile ? 36 : 44,
                minHeight: 44,
                px: isSmallMobile ? 1 : 1.5,
                fontSize: isSmallMobile ? '0.7rem' : '0.8rem',
              }}
              variant="outlined"
              onClick={() =>
                setLocale(
                  locale === 'ru' ? 'en' : locale === 'en' ? 'ky' : 'ru'
                )
              }
            >
              {locale.toUpperCase()}
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile menu drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: isSmallMobile ? '85vw' : 280,
            maxWidth: 320,
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Furniture Excellence
            </Typography>
            {user && (
              <Chip
                label={getUserRoleDisplayName()}
                color={userType === 'b2b' ? 'primary' : 'secondary'}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <Divider />
          <List sx={{ py: 1 }}>
            <ListItem
              button
              component={Link}
              to="/"
              onClick={toggleMobileMenu}
              sx={{ minHeight: 52, py: 1.5 }}
            >
              <ListItemText
                primary={t('nav.home')}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>
            {navigationItems.map((item) => (
              <ListItem
                key={item.path}
                button
                component={item.external ? 'a' : Link}
                {...(item.external
                  ? {
                      href: item.path,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                  : { to: item.path })}
                onClick={toggleMobileMenu}
                sx={{ minHeight: 52, py: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
          <Divider />
          <List sx={{ py: 1 }}>
            {isAuthenticated ? (
              <>
                <ListItem
                  button
                  component={Link}
                  to="/profile"
                  onClick={toggleMobileMenu}
                  sx={{ minHeight: 52, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      src={getAvatarSrc(user?.avatar)}
                      sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}
                    >
                      {user?.firstName?.[0]?.toUpperCase() || (
                        <AccountCircle fontSize="small" />
                      )}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={t('header.profile')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/orders"
                  onClick={toggleMobileMenu}
                  sx={{ minHeight: 52, py: 1.5 }}
                >
                  <ListItemText
                    primary={t('header.myOrders')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    sx={{ pl: 5 }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    handleLogout();
                    toggleMobileMenu();
                  }}
                  sx={{ minHeight: 52, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.logout')}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color: 'error.main',
                    }}
                  />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem
                  button
                  component={Link}
                  to="/login"
                  onClick={toggleMobileMenu}
                  sx={{ minHeight: 52, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Login />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.login')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/register"
                  onClick={toggleMobileMenu}
                  sx={{ minHeight: 52, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonAdd />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('header.register')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* B2B notice banner */}
      {userType === 'b2b' && hasB2BAccess() && (
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            py: 0.5,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="body2">
              🏢 B2B Режим - Специальные цены и условия для бизнеса
            </Typography>
          </Container>
        </Box>
      )}

      {/* B2B Application pending notice */}
      {user && user.role === 'b2b_candidate' && (
        <Box
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            py: 0.5,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="body2">
              ⏳ Ваша заявка на B2B статус рассматривается. Мы свяжемся с вами в
              ближайшее время.
            </Typography>
          </Container>
        </Box>
      )}
    </>
  );
};

export default Header;
